import { getBusinessSubTypeLabel } from "@/utils";
import React from "react";

// Add new props: setSelectedPlansAndBilling, setSelectedPlanIndex, getValues, setValue
const Summary = ({
  setCurrentStep,
  selectedPlansAndBilling,
  selectedPlanIndex, // Added: to update this state as well
  setSelectedPlansAndBilling,
  setSelectedPlanIndex,
  getValues,
  setValue,
}) => {
  // Calculate the total amount
  const totalAmount = Object.values(selectedPlansAndBilling).reduce(
    (acc, plan) => {
      // Ensure value exists and has a price before adding
      return acc + (plan.value?.price || 0);
    },
    0
  );

  // --- NEW FUNCTIONALITY ---
  const handleDeletePlan = (businessTypeToDelete) => {
    // 1. Update selectedPlansAndBilling state
    const newSelectedPlansAndBilling = { ...selectedPlansAndBilling };
    delete newSelectedPlansAndBilling[businessTypeToDelete];
    setSelectedPlansAndBilling(newSelectedPlansAndBilling);

    // 2. Update selectedPlanIndex state (if it tracks the index of selected plans)
    const newSelectedPlanIndex = { ...selectedPlanIndex };
    delete newSelectedPlanIndex[businessTypeToDelete];

    console.log({ newSelectedPlanIndex, selectedPlanIndex });
    // setSelectedPlanIndex(newSelectedPlanIndex);

    // 3. Update the 'type' field in the form (e.g., react-hook-form)
    // let updatedFormTypes = getValues("type") || [];
    // updatedFormTypes = updatedFormTypes.filter(
    //   (item) => item !== businessTypeToDelete
    // );
    // setValue("type", updatedFormTypes, { shouldValidate: true });
  };
  // --- END NEW FUNCTIONALITY ---

  // Helper function to render a single plan summary
  const renderPlanSummary = (businessType, planDetails) => {
    if (!planDetails || !planDetails.value) {
      return (
        <div
          key={businessType}
          className="mb-8 p-6 bg-red-50 border border-red-200 rounded-xl"
        >
          <h3 className="text-xl font-bold text-red-800 mb-4 flex justify-between items-center">
            Plan for "{getBusinessSubTypeLabel(businessType)}" - Not Selected
            {/* Optionally add delete button even for not-selected if user should explicitly clear it */}
            <button
              onClick={() => handleDeletePlan(businessType)}
              className="text-red-500 hover:text-red-700 ml-4 p-2 rounded-full hover:bg-red-100 transition-colors"
              aria-label={`Remove ${getBusinessSubTypeLabel(businessType)}`}
            >
              <i className="fa fa-times text-lg"></i>
            </button>
          </h3>
          <p className="text-red-700">
            No plan was selected for this business type. Please go back to
            choose a plan.
          </p>
        </div>
      );
    }

    const isYearly = planDetails.freq === "yearly";
    const displayedPrice = planDetails.value.price;
    const tierName = planDetails.tiers;

    const savingsMessage = isYearly ? "Save 20%" : null; // Placeholder for savings

    return (
      <div
        key={businessType}
        className=" rounded-xl border border-gray-200 mb-8 last:mb-0"
      >
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div className="w-full">
            <h3 className="text-xl justify-between md:text-2xl font-bold text-gray-800 mb-1 flex items-center gap-4">
              <div>
                Plan for{" "}
                <span className="text-red-600">
                  "{getBusinessSubTypeLabel(businessType)}"
                </span>
              </div>

              <button
                onClick={() => handleDeletePlan(businessType)}
                className="inline-flex group items-center gap-2 text-red-600 hover:text-white bg-red-100 hover:bg-red-600 px-3 py-1.5 rounded-md text-sm transition-colors"
                aria-label={`Remove plan for ${getBusinessSubTypeLabel(
                  businessType
                )}`}
              >
                <i className="fa fa-trash" aria-hidden="true"></i>
                <span className="group-hover:text-white">Remove</span>
              </button>
            </h3>
            <div className="inline-block bg-red-100 text-red-700 font-bold rounded-full px-4 py-1 text-sm uppercase tracking-wide mt-1">
              {tierName || "N/A"}
            </div>
          </div>
        </div>

        <div className="space-y-4 border-t border-gray-200 pt-6">
          <div className="flex justify-between items-center">
            <div>
              <h4 className="font-medium text-gray-700">Base Plan Cost</h4>
              <p className="text-sm text-gray-500 p-0">
                {isYearly
                  ? "Annual subscription (12 months)"
                  : "Monthly subscription"}
              </p>

              {savingsMessage && (
                <div className="bg-green-100 text-green-700 rounded-lg px-3 py-1 text-sm font-medium">
                  {savingsMessage}
                </div>
              )}
            </div>
            <span className="font-medium text-gray-800">
              ${displayedPrice?.toFixed(2)}
            </span>
          </div>

          <div className="flex justify-between items-center pt-4 border-t border-gray-100">
            <div>
              <h4 className="font-medium text-gray-700">Billing Cycle</h4>
              <p className="text-sm text-gray-500 !mb-1">
                {isYearly ? "Billed annually" : "Billed monthly"}
              </p>
            </div>
            <span className="text-sm text-gray-600">
              {isYearly ? "Per year" : "Per month"}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto p-6 md:p-8 bg-white rounded-2xl shadow-lg border border-gray-100">
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
          Order Summary
        </h2>
        <p className="text-gray-600 text-lg">
          Review your selected plans for all business types before proceeding to
          payment.
        </p>
      </div>

      {/* Render summary for each business type */}
      {Object.entries(selectedPlansAndBilling).length > 0 ? (
        Object.entries(selectedPlansAndBilling).map(
          ([businessType, planDetails]) =>
            renderPlanSummary(businessType, planDetails)
        )
      ) : (
        <div className="text-center p-8 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-lg text-gray-600 font-medium">
            No plans selected yet. Please go back and select some plans.
          </p>
          <button
            className="mt-6 px-6 py-3.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition shadow-md"
            onClick={() => setCurrentStep(2)} // Adjust to the step where plans are selected
          >
            Go to Plan Selection
          </button>
        </div>
      )}

      {/* Total Amount Section - Only show if there are selected plans */}
      {Object.keys(selectedPlansAndBilling).length > 0 && (
        <div className="bg-gradient-to-r from-red-50 to-white p-6 rounded-xl border border-red-200 mt-10">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-semibold text-red-800">
              Overall Total
            </h3>
            <div className="text-right">
              <div className="text-3xl font-bold text-red-900">
                ${totalAmount.toFixed(2)}
              </div>
              <p className="text-sm text-gray-600">
                This total will be charged{" "}
                {Object.keys(selectedPlansAndBilling).length > 1
                  ? "based on your selections"
                  : "according to your plan"}
                .
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-4 justify-between mt-10 pt-6 border-t border-gray-200">
        <button
          className="px-6 py-3.5 bg-white text-gray-700 font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition shadow-sm w-full sm:w-auto"
          onClick={() => setCurrentStep(2)} // Assuming step 2 is "Select Business Types" or "Select Payment Plan"
        >
          Back to Plans
        </button>
        <button
          className="px-6 py-3.5 bg-gradient-to-r from-red-600 to-red-700 text-white font-medium rounded-lg hover:from-red-700 hover:to-red-800 transition shadow-md w-full sm:w-auto"
          onClick={() => setCurrentStep(4)}
          disabled={Object.keys(selectedPlansAndBilling).length === 0} // Disable if no plans selected
        >
          Continue to Payment
        </button>
      </div>
    </div>
  );
};

export default Summary;
